/**
 * What one look takes in, for the quiet-path gate: the cells of the 1 m grid where a CRAWLING
 * man would be seen. The rules are `perceive`'s (src/sim/enemies/perception.ts): in range, inside
 * the cone (or inside the sense range), not crawling in a depression the watcher is outside of,
 * and with a clear line of sight to a target 0.4 m high.
 *
 * Line of sight is the sim's own geometry (`segmentHit`, and `lineOfSight`'s height rule), made
 * cheap enough to ask a few hundred thousand times: colliders that could never hide a crawler
 * are dropped, the rest are bucketed on a coarse grid, and for each look the ones in range are
 * binned by bearing, so a target is only tested against what lies in its own direction.
 */

import { atan2, cos, PI, sin, TAU } from '../../../sim/math';
import type { Collider } from '../../../sim/props';
import { segmentHit } from '../../../sim/statics';
import type { MissionDef } from '../../../sim/types';
import { QUIET } from '../../tuning';
import type { Look } from './model';

const BUCKET = 8;
const BINS = 64;

/** Cells whose centre lies in a depression: the sim's `inTrench`, rasterised one trench segment at a time. */
export function trenchCells(trenches: MissionDef['trenches'], width: number, height: number): Uint8Array {
  const out = new Uint8Array(width * height);
  for (const t of trenches) {
    const half = t.width / 2;
    for (let i = 1; i < t.points.length; i++) {
      const a = t.points[i - 1];
      const b = t.points[i];
      if (!a || !b) continue;
      const vx = b[0] - a[0];
      const vz = b[1] - a[1];
      const len2 = vx * vx + vz * vz;
      const z1 = Math.min(height - 1, Math.floor(Math.max(a[1], b[1]) + half));
      const x1 = Math.min(width - 1, Math.floor(Math.max(a[0], b[0]) + half));
      for (let z = Math.max(0, Math.floor(Math.min(a[1], b[1]) - half)); z <= z1; z++) {
        for (let x = Math.max(0, Math.floor(Math.min(a[0], b[0]) - half)); x <= x1; x++) {
          const px = x + 0.5 - a[0];
          const pz = z + 0.5 - a[1];
          const f = len2 === 0 ? 0 : Math.min(1, Math.max(0, (px * vx + pz * vz) / len2));
          const dx = px - vx * f;
          const dz = pz - vz * f;
          if (dx * dx + dz * dz <= half * half) out[z * width + x] = 1;
        }
      }
    }
  }
  return out;
}

export class SightIndex {
  private readonly colliders: Collider[] = [];
  private readonly reach: number[] = [];
  private readonly buckets: number[][] = [];
  private readonly across: number;
  private readonly stamp: Int32Array;
  private query = 0;
  private readonly bins: number[][] = [];

  constructor(
    all: readonly Collider[],
    private readonly width: number,
    private readonly height: number,
    /** 1 where a depression hides a crawling man. */
    private readonly trench: Uint8Array,
  ) {
    this.across = Math.ceil(width / BUCKET);
    const down = Math.ceil(height / BUCKET);
    for (let i = 0; i < this.across * down; i++) this.buckets.push([]);
    for (let i = 0; i < BINS; i++) this.bins.push([]);
    for (const c of all) {
      // `lineOfSight` stops a ray that is below a collider's top: nothing this low ever stops one to a crawler.
      if (!c.blocksSight || c.h <= QUIET.crawlHeight) continue;
      const ex = c.shape === 'circle' ? c.r : c.hx;
      const ez = c.shape === 'circle' ? c.r : c.hz;
      const index = this.colliders.length;
      this.colliders.push(c);
      this.reach.push(Math.sqrt(ex * ex + ez * ez));
      const bx1 = Math.min(this.across - 1, Math.floor((c.x + ex) / BUCKET));
      const bz1 = Math.min(down - 1, Math.floor((c.z + ez) / BUCKET));
      for (let bz = Math.max(0, Math.floor((c.z - ez) / BUCKET)); bz <= bz1; bz++) {
        for (let bx = Math.max(0, Math.floor((c.x - ex) / BUCKET)); bx <= bx1; bx++) {
          this.buckets[bz * this.across + bx]?.push(index);
        }
      }
    }
    this.stamp = new Int32Array(this.colliders.length).fill(-1);
  }

  /** Sort the colliders within the look's range into the bearings they span, as seen from it. */
  private binColliders(look: Look): void {
    for (const bin of this.bins) bin.length = 0;
    this.query++;
    const r = look.range;
    const down = this.buckets.length / this.across;
    const bx1 = Math.min(this.across - 1, Math.floor((look.x + r) / BUCKET));
    const bz1 = Math.min(down - 1, Math.floor((look.z + r) / BUCKET));
    for (let bz = Math.max(0, Math.floor((look.z - r) / BUCKET)); bz <= bz1; bz++) {
      for (let bx = Math.max(0, Math.floor((look.x - r) / BUCKET)); bx <= bx1; bx++) {
        for (const index of this.buckets[bz * this.across + bx] ?? []) {
          if (this.stamp[index] === this.query) continue;
          this.stamp[index] = this.query;
          const c = this.colliders[index];
          if (!c || c.propId === look.ignoreProp) continue;
          const size = this.reach[index] ?? 0;
          const dx = c.x - look.x;
          const dz = c.z - look.z;
          const d = Math.sqrt(dx * dx + dz * dz);
          if (d - size > r) continue;
          // Half the bearing it spans: asin(size / d) is never more than (PI / 2) * size / d.
          const spread = d <= size * 1.05 ? PI : Math.min(PI, (PI / 2) * (size / d));
          const centre = atan2(dx, dz);
          const first = Math.floor(((centre - spread + PI) / TAU) * BINS) - 1;
          const last = Math.floor(((centre + spread + PI) / TAU) * BINS) + 1;
          const span = Math.min(BINS, last - first + 1);
          for (let k = 0; k < span; k++) this.bins[(((first + k) % BINS) + BINS) % BINS]?.push(index);
        }
      }
    }
  }

  /** The sim's line of sight from the look to a crawling man at (x, z), against the colliders on that bearing. */
  private clear(look: Look, x: number, z: number, bearing: number): boolean {
    const bin = this.bins[Math.min(BINS - 1, Math.max(0, Math.floor(((bearing + PI) / TAU) * BINS)))] ?? [];
    for (const index of bin) {
      const c = this.colliders[index];
      if (!c) continue;
      const hit = segmentHit(c, look.x, look.z, x, z);
      if (!hit) continue;
      const drop = QUIET.crawlHeight - look.eye;
      if (Math.min(look.eye + drop * hit.enter, look.eye + drop * hit.exit) < c.h) return false;
    }
    return true;
  }

  /** Is a man crawling in any of these cells seen by this look? For spot checks; `visible` does whole cones. */
  seesAny(look: Look, cells: readonly number[]): boolean {
    if (look.smell !== true) this.binColliders(look);
    const fx = sin(look.facing);
    const fz = cos(look.facing);
    const cosHalf = cos(look.half);
    for (const cell of cells) {
      const cx = cell % this.width;
      if (this.test(look, fx, fz, cosHalf, cx, (cell - cx) / this.width)) return true;
    }
    return false;
  }

  /** The sim's `inTrench`, from the raster. */
  inTrench(x: number, z: number): boolean {
    const cx = Math.floor(x);
    const cz = Math.floor(z);
    return cx >= 0 && cz >= 0 && cx < this.width && cz < this.height && this.trench[cz * this.width + cx] !== 0;
  }

  private test(look: Look, fx: number, fz: number, cosHalf: number, cx: number, cz: number): boolean {
    const dx = cx + 0.5 - look.x;
    const dz = cz + 0.5 - look.z;
    const d2 = dx * dx + dz * dz;
    if (d2 > look.range * look.range) return false;
    if (this.trench[cz * this.width + cx] !== 0 && !look.inTrench) return false;
    if (d2 > look.sense * look.sense && look.half < PI && dx * fx + dz * fz < Math.sqrt(d2) * cosHalf) return false;
    // A dog smells him through a wall as well as round a corner (ROUND8, nose.ts).
    return look.smell === true || this.clear(look, cx + 0.5, cz + 0.5, atan2(dx, dz));
  }

  /** Append to `out` every cell of `wanted` in which this look sees a crawling man. */
  visible(look: Look, wanted: Uint8Array, out: number[]): void {
    if (look.smell !== true) this.binColliders(look);
    const fx = sin(look.facing);
    const fz = cos(look.facing);
    const cosHalf = cos(look.half);
    const x1 = Math.min(this.width - 1, Math.floor(look.x + look.range));
    const z1 = Math.min(this.height - 1, Math.floor(look.z + look.range));
    for (let cz = Math.max(0, Math.floor(look.z - look.range)); cz <= z1; cz++) {
      // Only the chord of the range circle on this row.
      const dz = cz + 0.5 - look.z;
      const chord = Math.sqrt(Math.max(0, look.range * look.range - dz * dz));
      const xEnd = Math.min(x1, Math.floor(look.x + chord));
      for (let cx = Math.max(0, Math.floor(look.x - chord)); cx <= xEnd; cx++) {
        const cell = cz * this.width + cx;
        if (wanted[cell] !== 0 && this.test(look, fx, fz, cosHalf, cx, cz)) out.push(cell);
      }
    }
  }
}
